/**
 * Phase2CapacityTable.tsx
 * "Team Allocation & Weekly Capacity" — the single merged staffing table
 * (staffing-allocation follow-up 2026-07-13). Per (employee, week) you BOTH edit
 * the allocation % (the INPUT) and see the resulting utilization band (the
 * OUTPUT) in the same matrix-style TILE, aligned column-for-column under the
 * Task Weekly Schedule and sharing its week-hover beam.
 *
 * Tile restyle (2026-07-13, Gigi feedback): the confusing per-day "N tests"
 * count cells are GONE. Each week is now a clean tile mirroring the Employee
 * Schedule matrix — utilization band + % + inline stepper, with the day-off /
 * conflict signal ON the tile. Clicking a tile expands an inline day-by-day
 * WO/task breakdown (CapacityWeekExpand) instead of showing per-day counts.
 *
 * Rows = distinct employees in the plan (assigned to a task OR on the roster).
 * Columns = the same BST week columns as Phase2WeeklyGrid (Employee · Role ·
 * Week1…N). A trailing "add person" row + per-week "team total" footer complete
 * the roster controls.
 *
 * Editing a stepper writes through PR A's /roster endpoint via useSourceRoster
 * (optimistic + debounced); over-100% warns but never blocks (D4); a day-off
 * collision (409) surfaces inline. The band recolors on the next capacity
 * refetch (refreshKey, bumped by the parent's onAllocationChanged).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import {
  fetchEmployeeWeeks,
  fetchEmployeeWeekDetail,
  type EmployeeWeekCell,
  type CrossScenarioSource,
} from '../../../../services/scheduleEmployeeSchedule';
import { listAssignableUsers, type AssignableUser } from '../../../../services/scheduleAssignments';
import type { RosterSource, RosterWeek } from '../../../../services/scheduleRoster';
import { CapacityEmployeeRow, type WeekExpandDetail } from './CapacityEmployeeRow';
import { CapacityAddPersonRow } from './CapacityAddPersonRow';
import { CapacityGridHeader } from './CapacityGridHeader';
import { CapacityTeamTotalRow } from './CapacityTeamTotalRow';
import { Phase2CapacityLegend } from './Phase2CapacityLegend';
import { useSourceRoster } from './useSourceRoster';
import { mergeEmployeeRows } from './staffRosterRows';
import { weekStartsInRange } from '../../../../utils/weekUtils';
import {
  EMP_COL_W,
  ROLE_COL_W,
  WEEK_W,
} from './phase2CapacityShared';
import type { TaskWithBookings, TaskAssignee } from '../../../../services/scheduleTasks';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface Phase2CapacityTableProps {
  tasks: TaskWithBookings[];
  /** Map of ScheduleTaskKey → TaskAssignee[] — same data as Phase2WeeklyGrid. */
  taskAssignmentMap: Record<string, TaskAssignee[]>;
  scenarioKey: string | null;
  /** The plan's source (WO / Quote) — the roster hangs off it (D3). */
  source: RosterSource | null;
  /** The plan's source key (WorkOrderKey / QuoteKey). */
  sourceKey: string | null;
  /** Plan locked (Submitted / Archived) → read-only: no steppers, add, or remove. */
  disabled?: boolean;
  /** Notify the parent (bumps the capacity refetch + live schedules) on a roster change. */
  onAllocationChanged?: () => void;
  /**
   * Increment to trigger a capacity re-fetch from the parent (Phase 6C.5 wire-up).
   * On 6C.4 this is fetched once on mount and whenever refreshKey changes.
   */
  refreshKey?: number;
  /** Stat-holiday ISO dates — so holiday day-columns render as non-working. */
  holidays?: readonly string[];
  /**
   * The week (Saturday ISO) hovered anywhere on the shared rail — lights up the SAME
   * week here as in the task grid above (the synchronized "beam"). Null = none.
   */
  hoveredWeek?: string | null;
  /** Report the week the pointer is over (or null) for the shared beam. */
  onHoverWeek?: (weekStart: string | null) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute the sorted week-start Saturdays spanning all tasks. */
function computeWeekColumns(tasks: TaskWithBookings[]): string[] {
  let minStart: string | null = null;
  let maxEnd: string | null = null;
  for (const t of tasks) {
    if (t.StartDate && (!minStart || t.StartDate < minStart)) minStart = t.StartDate;
    if (t.EndDate && (!maxEnd || t.EndDate > maxEnd)) maxEnd = t.EndDate;
  }
  if (!minStart || !maxEnd) return [];
  return weekStartsInRange(minStart, maxEnd);
}

/** Look up a user × week cell (undefined if not found). */
function findCell(
  cells: EmployeeWeekCell[],
  userKey: string,
  weekStart: string,
): EmployeeWeekCell | undefined {
  const lk = userKey.toLowerCase();
  return cells.find((c) => c.userKey.toLowerCase() === lk && c.weekStart === weekStart);
}

/** Cache key for an expanded (person, week) detail. */
function detailKey(userKey: string, weekStart: string): string {
  return `${userKey.toLowerCase()}::${weekStart}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase2CapacityTable: React.FC<Phase2CapacityTableProps> = ({
  tasks,
  taskAssignmentMap,
  scenarioKey,
  source,
  sourceKey,
  disabled = false,
  onAllocationChanged,
  refreshKey = 0,
  holidays = [],
  hoveredWeek,
  onHoverWeek,
}) => {
  const theme = useTheme();
  const holidaySet = React.useMemo(() => new Set(holidays), [holidays]);

  // ── Derived data ────────────────────────────────────────────────────────
  const weekColumns = computeWeekColumns(tasks);

  // Content signature of the assignments so the capacity API re-fetches on ANY
  // change — add/remove, percent edit, week-span, or per-machine move.
  const assignmentSig = React.useMemo(() => {
    const parts: string[] = [];
    for (const [taskKey, rows] of Object.entries(taskAssignmentMap)) {
      for (const r of rows) {
        parts.push(`${taskKey}:${r.UserKey}:${r.MachineBookingKey ?? ''}:${r.WeekStart}:${r.Percent}`);
      }
    }
    return parts.sort().join('|');
  }, [taskAssignmentMap]);

  // ── Roster (the allocation INPUT) — the standalone panel's logic, now here ──
  const {
    weeks: rosterWeeks,
    loadError: rosterLoadError,
    mutationError,
    clearMutationError,
    setAllocation,
    removeMember,
  } = useSourceRoster({
    source,
    sourceKey,
    scenarioKey,
    enabled: !disabled,
    onAllocationChanged,
  });

  // (user::week) → roster entry, and per-week team total (Σ this-plan %).
  const rosterByUserWeek = useMemo(() => {
    const m = new Map<string, RosterWeek['entries'][number]>();
    for (const w of rosterWeeks) {
      for (const e of w.entries) m.set(`${e.userKey.toLowerCase()}::${w.weekStart}`, e);
    }
    return m;
  }, [rosterWeeks]);

  const teamTotalByWeek = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of rosterWeeks) {
      m.set(w.weekStart, w.entries.reduce((s, e) => s + (e.percent || 0), 0));
    }
    return m;
  }, [rosterWeeks]);

  // Employee rows = assigned ∪ rostered.
  const employeeRows = mergeEmployeeRows(taskAssignmentMap, rosterWeeks);

  // ── Capacity data (the utilization OUTPUT) from the API ────────────────────
  const [capacityCells, setCapacityCells] = useState<EmployeeWeekCell[]>([]);
  // Per-user (lowercased key) attribution for cross-scenario soft holds.
  const [crossSources, setCrossSources] = useState<Record<string, CrossScenarioSource[]>>({});
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Track the last fetch request to avoid stale-update races.
  const fetchIdRef = useRef(0);

  const fetchCapacity = useCallback(async () => {
    // Fetch whenever there are weeks — even with nobody assigned yet, so the
    // add-person picker can annotate candidates with their load elsewhere (D6).
    if (weekColumns.length === 0) {
      setCapacityCells([]);
      return;
    }

    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setLoadError(null);

    try {
      const startWeek = weekColumns[0];
      const weekCount = weekColumns.length;
      const result = await fetchEmployeeWeeks({
        startWeek,
        weekCount,
        scenarioKey: scenarioKey ?? undefined,
      });
      if (fetchIdRef.current !== fetchId) return; // stale
      setCapacityCells(result.cells);
      setCrossSources(result.crossScenarioSources ?? {});
    } catch (err) {
      if (fetchIdRef.current !== fetchId) return;
      setLoadError(
        err instanceof Error ? err.message : 'Failed to load capacity data.',
      );
    } finally {
      if (fetchIdRef.current === fetchId) setLoading(false);
    }
  }, [weekColumns.join(','), assignmentSig, scenarioKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void fetchCapacity();
  }, [fetchCapacity, refreshKey]);

  // Assignable users for the add-person picker — loaded once.
  const [candidates, setCandidates] = useState<AssignableUser[]>([]);
  useEffect(() => {
    let cancelled = false;
    listAssignableUsers()
      .then((u) => { if (!cancelled) setCandidates(u); })
      .catch(() => { if (!cancelled) setCandidates([]); });
    return () => { cancelled = true; };
  }, []);

  // PEAK weekly utilization % per user (for "suggest who's free" ordering + hint).
  const allocPeakByUser = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of capacityCells) {
      const k = c.userKey.toLowerCase();
      m.set(k, Math.max(m.get(k) ?? 0, c.allocationPercent ?? 0));
    }
    return m;
  }, [capacityCells]);

  const existingKeys = useMemo(
    () => new Set(employeeRows.map((e) => e.userKey.toLowerCase())),
    [employeeRows],
  );

  // ── Inline expand (day-by-day WO/task breakdown) ────────────────────────────
  const [expanded, setExpanded] = useState<{ userKey: string; weekStart: string } | null>(null);
  const [detailByKey, setDetailByKey] = useState<Record<string, WeekExpandDetail>>({});
  const detailFetchId = useRef(0);

  const loadDetail = useCallback(async (userKey: string, weekStart: string) => {
    const k = detailKey(userKey, weekStart);
    const fetchId = ++detailFetchId.current;
    setDetailByKey((m) => ({ ...m, [k]: { assignments: [], sourceAllocations: [], isLoading: true, error: null } }));
    try {
      const detail = await fetchEmployeeWeekDetail(userKey, weekStart, scenarioKey ?? undefined);
      if (detailFetchId.current !== fetchId) return;
      setDetailByKey((m) => ({
        ...m,
        [k]: { assignments: detail.assignments, sourceAllocations: detail.sourceAllocations ?? [], isLoading: false, error: null },
      }));
    } catch (err) {
      if (detailFetchId.current !== fetchId) return;
      setDetailByKey((m) => ({
        ...m,
        [k]: { assignments: [], sourceAllocations: [], isLoading: false, error: err instanceof Error ? err.message : 'Failed to load details.' },
      }));
    }
  }, [scenarioKey]);

  const handleToggleExpand = useCallback((userKey: string, weekStart: string) => {
    const isOpen = expanded?.userKey === userKey && expanded?.weekStart === weekStart;
    if (isOpen) {
      setExpanded(null);
      return;
    }
    setExpanded({ userKey, weekStart });
    void loadDetail(userKey, weekStart);
  }, [expanded, loadDetail]);

  // Any capacity refetch (roster edit / scenario change) can move the open
  // week's day bars → drop the cache and re-fetch the open one so it stays true.
  useEffect(() => {
    setDetailByKey({});
    if (expanded) void loadDetail(expanded.userKey, expanded.weekStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentSig, refreshKey, scenarioKey]);

  // ── Empty / no-weeks guard ────────────────────────────────────────────────
  if (tasks.length === 0 || weekColumns.length === 0) return null;

  const editable = !disabled;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {loadError && (
        <Alert severity="warning" onClose={() => setLoadError(null)} sx={{ mb: 1 }}>
          {loadError}
        </Alert>
      )}
      {rosterLoadError && (
        <Alert severity="warning" sx={{ mb: 1 }}>{rosterLoadError}</Alert>
      )}

      <Box
        onMouseLeave={() => onHoverWeek?.(null)}
        sx={{
          border: `1px solid ${theme.border.color}`,
          borderRadius: `${theme.border.radius}px`,
          // No own scroll — the shared rail container (Phase2Panel) owns the single
          // horizontal scrollbar for both grids (2026-06-23).
          overflow: 'visible',
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
          width: 'fit-content',
        }}
      >
        {/* Stale-while-revalidate corner spinner during refetch (no full-table blank). */}
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              zIndex: 4,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.background.paper, 0.85),
              boxShadow: `0 0 0 1px ${theme.border.color}`,
            }}
          >
            <CircularProgress size={12} thickness={5} />
            <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
              syncing…
            </Typography>
          </Box>
        )}

        <Table
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: EMP_COL_W + ROLE_COL_W + weekColumns.length * WEEK_W,
          }}
        >
          {/* The capacity grid keeps its OWN day-label header (Employee/Role + Sa–Fr per
              week) in addition to the merged sticky rail header at the very top. */}
          <CapacityGridHeader
            weekColumns={weekColumns}
            holidaySet={holidaySet}
            hoveredWeek={hoveredWeek}
            onHoverWeek={onHoverWeek}
          />

          <TableBody>
            {employeeRows.map((emp, rowIdx) => {
              const rowBg = rowIdx % 2 === 1 ? theme.table.stripeBg : undefined;
              const lk = emp.userKey.toLowerCase();
              const rowExpandedWeek =
                expanded && expanded.userKey.toLowerCase() === lk ? expanded.weekStart : null;
              return (
                <CapacityEmployeeRow
                  key={emp.userKey}
                  emp={emp}
                  rowBg={rowBg}
                  weekColumns={weekColumns}
                  holidaySet={holidaySet}
                  cellFor={(ws) => findCell(capacityCells, emp.userKey, ws)}
                  sourcePercentFor={(ws) => rosterByUserWeek.get(`${lk}::${ws}`)?.percent ?? 0}
                  crossSources={crossSources[lk] ?? []}
                  hoveredWeek={hoveredWeek}
                  onHoverWeek={onHoverWeek}
                  editable={editable}
                  expandedWeek={rowExpandedWeek}
                  expandDetail={rowExpandedWeek ? detailByKey[detailKey(emp.userKey, rowExpandedWeek)] : undefined}
                  onSetPercent={(ws, p) => setAllocation(emp.userKey, emp.displayName, ws, p)}
                  onRemove={() => {
                    for (const ws of weekColumns) {
                      if (rosterByUserWeek.has(`${lk}::${ws}`)) removeMember(emp.userKey, ws);
                    }
                  }}
                  onToggleExpand={(ws) => handleToggleExpand(emp.userKey, ws)}
                />
              );
            })}

            {/* Per-week team total (this plan's Σ allocation %). */}
            <CapacityTeamTotalRow
              weekColumns={weekColumns}
              teamTotalByWeek={teamTotalByWeek}
              hoveredWeek={hoveredWeek}
              onHoverWeek={onHoverWeek}
            />

            {/* Add person (hidden when the plan is locked). */}
            {editable && (
              <CapacityAddPersonRow
                candidates={candidates}
                existingKeys={existingKeys}
                allocByUser={allocPeakByUser}
                weekColSpan={weekColumns.length * 7}
                onAdd={(userKey, displayName, percent) => {
                  for (const ws of weekColumns) setAllocation(userKey, displayName, ws, percent);
                }}
              />
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Legend — utilization bands + task / absence / day-off / conflict keys. */}
      <Phase2CapacityLegend />

      {/* Inline roster mutation error (day-off block Q3B / lock / network) — verbatim. */}
      <Snackbar
        open={Boolean(mutationError)}
        autoHideDuration={7000}
        onClose={clearMutationError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={clearMutationError} sx={{ width: '100%' }}>
          {mutationError}
        </Alert>
      </Snackbar>
    </>
  );
};
